<script setup lang="ts">
import ProgramEndedView from '@/views/ProgramEndedView.vue';
// NPM
import { onBeforeMount, onErrorCaptured, ref, watch } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { AxiosError } from 'axios';
import { readJwtToken } from '@/services/auth-tokens';
import NoAccessView from '@/views/NoAccessView.vue';

// Components
import { BaseAlert, BaseLoader } from '@magma-app/magma-lapilli';
import CallTextModal from '@/components/modals/CallTextModal.vue';
import BaseSupport from '@/components/bases/BaseSupport.vue';
import ErrorView from '@/views/ErrorView.vue';
import ManageChallengesModal from '@/components/modals/ManageChallengesModal.vue';
import ManageRewardsModal from '@/components/modals/ManageRewardsModal.vue';
import ManageUgcsModal from '@/components/modals/ManageUgcsModal.vue';
import FeedbackRelationModal from '@/components/modals/FeedbackRelationModal.vue';
import ReportModal from '@/components/modals/ReportModal.vue';
import ShareModal from '@/components/modals/ShareModal.vue';
import SlideOverModal from '@/components/modals/SlideOverModal.vue';
import TheFooter from '@/components/shell/TheFooter.vue';
import TheNavbar from '@/components/shell/TheNavbar.vue';
import TouchpointsModal from '@/components/modals/TouchpointsModal.vue';
import QrCodeModal from '@/components/modals/QrCodeModal.vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';
import UtilityHelper from '@/helpers/utility';

// Stores
import { useAuthStore } from '@/stores/auth';
import { useCommonStore } from '@/stores/common';
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import { consumeReturnTo, saveReturnTo } from '@/services/return-to';
const authStore = useAuthStore();
const commonStore = useCommonStore();
const relationsStore = useRelationsStore();
const userStore = useUserStore();

// Plugins
const route = useRoute();
const router = useRouter();
const { locale } = useI18n({ useScope: 'global' });

const SUPPORTED_LOCALES = ['en', 'fr'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const normalizeLocale = (
  value?: string | null
): SupportedLocale | undefined => {
  if (!value) return undefined;
  return SUPPORTED_LOCALES.includes(value as SupportedLocale)
    ? (value as SupportedLocale)
    : undefined;
};

const getBrowserLocale = () => {
  const candidate =
    typeof navigator !== 'undefined' ? navigator.language.split('-')[0] : 'en';
  return normalizeLocale(candidate) || 'en';
};

const applyLocale = (value?: string | null) => {
  const normalized = normalizeLocale(value);
  if (!normalized || normalized === locale.value) {
    return;
  }
  locale.value = normalized;
};

// Reactive variables
const error = ref<Error | AxiosError | null>(null);
const errorCode = ref<number | null>(null);
const initialized = ref(false);
const sessionRestoreFailed = ref(false);
const retrySession = () => window.location.reload();

// Lifecycle hooks
onBeforeMount(async () => {
  try {
    if (['program-ended', 'no-access'].includes(route.name as string)) return;
    // Initialize JWT token
    const token = (route?.params.token || route?.query.token) as string;

    const isProspect =
      route.query.prospect === 'true' || route.meta.role === 'helpee' ||
      route?.path.includes('helpee-relation') ||
      route?.path.includes('start-relation/helpee') ||
      route?.path.includes('report-feedback') ||
      route?.path.includes('report/helpee/');

    userStore.isProspect = isProspect;

    if (token) {
      await authStore.getJwtToken(token, isProspect);

      // Clean the URL by removing the token query parameter for security
      if (route.query.token) {
        const cleanQuery = { ...route.query };
        delete cleanQuery.token;

        // The magic link usually already points at the page the visitor was
        // trying to reach. When it does not (the redirect was dropped, or they
        // logged in from another entry point), fall back to the target saved
        // before the login bounce.
        const returnTo = consumeReturnTo();
        const shouldReturn = returnTo && !returnTo.startsWith(`${route.path}?`)
          && returnTo !== route.path;

        // Replace current URL without the token parameter
        router.replace(
          shouldReturn
            ? returnTo
            : {
              path: route.path,
              query: Object.keys(cleanQuery).length > 0 ? cleanQuery : undefined,
            }
        );
      }
    }

    // Redirect to sign in when pages are accessed without a token
    const publicPages = [
      'helpee-campaigns',
      'helpee-signup',
      'expired-link',
      // reached right after a hard redirect that cleared the session
      'no-access',
      'login',
      'referred',
      'referred-sharing',
      'token',
      'helpee-thanks',
      'helpee-qualification',
      // legacy
      'helper-onboarding',
      'helper-signup',
      // end legacy
      'ambassador-signup-campaign',
      'ambassador-signup-teamspace',
      'reviews',
      'helpee-complete-signup',
      'public-reviews',
      'share-review',
      'helpee-finish',
      'helpee-waiting',
      'referral-invite',
      // IP-blocked page: public so a banned (and thus unauthenticated) visitor is
      // not bounced to /login before the blocked page can render.
      'blocked',
      'program-ended',
    ];
    const authRequired = !publicPages.includes(route?.name as string);
    let jwtToken = undefined;

    if (!route?.query.widget) {
      try {
        // Check for existing token in storage OR the newly authenticated token
        jwtToken =
          readJwtToken(userStore.isProspect ? 'prospect' : 'user', true) ||
          (authStore.jwtToken as string | undefined);
      } catch (e) {
        console.log('We don\'t have access to the local storage or cookies');
      }
    }

    if (authRequired && !jwtToken) {
      saveReturnTo(route?.fullPath);
      router.push({ name: 'login' });
      return; // Exit early to prevent further execution
    }

    if (
      !['expired-link', 'login', 'public-reviews', 'share-review'].includes(
        route?.name as string
      ) &&
      !(
        route?.name === 'report-relation-view' &&
        route?.params.userType === 'helpee'
      ) &&
      route?.meta.role != 'helpee' &&
      jwtToken &&
      !isProspect
    ) {
      await userStore.getUser();

      if (userStore.user?.language) {
        applyLocale(userStore.user.language);
      } else {
        locale.value = getBrowserLocale();
      }

      if (authStore.jwtToken && route?.name === 'token') {
        // Back to the page they were trying to reach, else home.
        const returnTo = consumeReturnTo();
        router.push(returnTo ?? { name: 'relations-empty-state' });
      }

      if (route?.meta.role != 'helpee') {
        await userStore.getCampaigns();
      }
    }

    // Redirect to homepage when rewards are disabled
    if (
      ['rewards', 'purchased-rewards'].includes(route?.name as string) &&
      !userStore.user?.organization?.rewardEnabled
    ) {
      router.push({ name: 'relations' });
    }

    if (route?.name === 'referred' && route?.params.referredUuid) {
      await authStore.getReferredSignUpData(route?.params.referredUuid as string);
    }

    initialized.value = true;

    if (
      ![
        'expired-link',
        'helpee-campaigns',
        'helpee-signup',
        'helpee-qualification',
        'login',
        'referred',
        'referred-sharing',
        'helpee-thanks',
        'helpee-complete-signup',
        'share-review',
        'public-reviews',
        'helpee-finish',
        'helpee-waiting',
      ].includes(route?.name as string) &&
      jwtToken
    ) {
      await userStore.getSupportData(
        Number(route?.params.relationId) || undefined
      );
    }

    // Load scripts
    const fathomUrl = import.meta.env.VITE_FATHOM_ANALYTICS_URL;

    if (fathomUrl) {
      try {
        await UtilityHelper.loadScript(fathomUrl, {
          'data-site': import.meta.env.VITE_FATHOM_ANALYTICS_KEY,
        });
      } catch (error) {
        console.error('Failed to load Fathom analytics script:', error);
      }
    }

    // Initialize analytics libraries
    AnalyticsHelper.initialize();
  } catch {
    sessionRestoreFailed.value = true;
  } finally {
    initialized.value = true;
  }
});

// Watchers
watch(route, async (newRoute) => {
  if (
    newRoute?.name === 'helpee-qualification' &&
    authStore.helpeeQualificationData?.language
  ) {
    console.log('ok');
    applyLocale(authStore.helpeeQualificationData.language);
  }
  if (
    ['helpee-campaigns', 'helpee-signup'].includes(newRoute?.name as string) &&
    authStore.helpeeSignUpData?.language
  ) {
    applyLocale(authStore.helpeeSignUpData.language);
  } else if (newRoute?.name === 'referred' && authStore.referredSignUpData) {
    locale.value = getBrowserLocale();
  } else if (
    newRoute?.name === 'referred-sharing' &&
    authStore.referredSharingSignUpData?.organization.languages
  ) {
    applyLocale(authStore.referredSharingSignUpData?.organization.languages[0]);
  } else if (
    ['helpee-relation-overview', 'report-relation-view'].includes(
      newRoute?.name as string
    )
  ) {
    locale.value = getBrowserLocale();
  } else if (
    ['expired-link', 'login'].includes(newRoute?.name as string) &&
    authStore.signInData
  ) {
    applyLocale(authStore.signInData.language);
  } else if (userStore.user?.language) {
    applyLocale(userStore.user.language);
  } else {
    locale.value = getBrowserLocale();
  }
});

watch(
  () => authStore.helpeeQualificationData?.language,
  (language) => {
    if (route?.name === 'helpee-qualification') {
      applyLocale(language);
    }
  }
);

watch(
  () => authStore.helpeeSignUpData?.language,
  (language) => {
    if (['helpee-campaigns', 'helpee-signup'].includes(route?.name as string)) {
      applyLocale(language);
    }
  }
);

watch(
  () => authStore.referredSharingSignUpData?.organization?.languages?.[0],
  (language) => {
    if (route?.name === 'referred-sharing') {
      applyLocale(language);
    }
  }
);

watch(
  () => authStore.signInData?.language,
  (language) => {
    if (['expired-link', 'login'].includes(route?.name as string)) {
      applyLocale(language);
    }
  }
);

// Listeners
onErrorCaptured((e: AxiosError) => {
  if ((e.response?.data as any)?.disabled === true) return false;
  if (
    e.config &&
    [
      '/sign-up-prospect/phone/verify-code',
      '/sign-up-prospect/referred/phone/verify-code',
    ].includes(e.config.url as string)
  ) {
    return false;
  }

  error.value = {
    message: e.message,
    name: e.name,
    stack: e.stack,
  };

  if (e.response) {
    errorCode.value = e.response.status;
  }

  return true;
});
</script>

<template>
  <ProgramEndedView v-if="$route.name === 'program-ended'" />
  <NoAccessView v-else-if="$route.name === 'no-access'" />
  <template v-else>
  <div v-if="sessionRestoreFailed" class="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
    <p role="alert">{{ $t('sessionRecovery.error') }}</p>
    <button class="rounded-lg bg-slate-900 px-4 py-2 text-white" @click="retrySession">{{ $t('sessionRecovery.retry') }}</button>
  </div>
  <div
    v-if="initialized && !sessionRestoreFailed"
    class="relative flex min-h-dvh flex-col bg-background-default"
    :class="{
      'pb-36 sm:pb-24': ['helper-abcd'].includes($route?.name as string)
    }"
  >
    <TheNavbar
      v-if="
        $route?.name != 'helpee-relation-overview' &&
        $route?.name != 'error' &&
        $route?.name != 'expired-link' &&
        $route?.name != 'login' &&
        $route?.name != 'referred-sharing' &&
        $route?.name != 'report-relation-view' &&
        $route?.name != 'helpee-signup' &&
        $route?.name != 'helpee-thanks' &&
        $route?.name != 'start-relation-helpee' &&
        $route?.name != 'start-relation-helper' &&
        $route?.name != 'helpee-qualification' &&
        $route?.name != 'helper-onboarding' &&
        $route?.name != 'helper-signup' &&
        $route?.name != 'ambassador-signup-campaign' &&
        $route?.name != 'ambassador-signup-teamspace' &&
        $route?.name != 'reviews' &&
        $route?.name != 'helpee-complete-signup' &&
        $route?.name != 'public-reviews' &&
        $route?.name != 'share-review' &&
        $route?.name != 'helpee-finish' &&
        $route?.name != 'referral-invite' &&
        $route?.name != 'helpee-waiting'
      "
      :class="{
        'lg:hidden':
          $route?.name === 'available-challenges' ||
          $route?.name === 'ongoing-challenges' ||
          $route?.name === 'validated-challenges' ||
          $route?.name === 'rejected-challenges' ||
          $route?.name === 'completed-challenges' ||
          $route?.name === 'available-events' ||
          $route?.name === 'ongoing-events' ||
          $route?.name === 'validated-events' ||
          $route?.name === 'rejected-events' ||
          $route?.name === 'completed-events' ||
          $route?.name === 'available-ugcs' ||
          $route?.name === 'ongoing-ugcs' ||
          $route?.name === 'validated-ugcs' ||
          $route?.name === 'rejected-ugcs' ||
          $route?.name === 'completed-ugcs' ||
          $route?.name === 'helper-moment-overview' ||
          $route?.name === 'helper-relation-overview' ||
          $route?.name === 'helper-status' ||
          $route?.name === 'purchased-rewards' ||
          $route?.name === 'referral' ||
          $route?.name === 'referral-invitations' ||
          $route?.name === 'relations' ||
          $route?.name === 'rewards' ||
          $route?.name === 'sharing' ||
          $route?.name === 'relations-empty-state'
      }"
    />

    <ErrorView
      v-if="error && !['expired-link', 'login'].includes(route?.name as string)"
      :error-code="errorCode"
      :is-logged="!!authStore.jwtToken"
    />
    <RouterView
      v-else-if="
        !error || ['expired-link', 'login'].includes(route?.name as string)
      "
      class="flex-1"
    />
  </div>

  <BaseLoader v-else class="min-h-screen" />

  <BaseSupport
    v-if="
      initialized &&
      ![
        'expired-link',
        'helpee-campaigns',
        'helpee-signup',
        'login',
        'referred',
        'share-review',
        'public-reviews'
      ].includes(route?.name as string) &&
      userStore.supportData
    "
    v-bind="commonStore.adminModal.props"
  />

  <BaseAlert :key="commonStore.alert.id" v-bind="commonStore.alert.props" />

  <CallTextModal v-bind="commonStore.callTextModal.props" />

  <ManageChallengesModal v-bind="commonStore.manageChallengesModal.props" />

  <ManageRewardsModal v-bind="commonStore.manageRewardsModal.props" />

  <ManageUgcsModal v-bind="commonStore.manageUgcsModal.props" />

  <FeedbackRelationModal v-bind="commonStore.feedbackRelationModal.props" />

  <TouchpointsModal v-bind="commonStore.touchpointsModal.props" />

  <ReportModal v-bind="commonStore.reportModal.props" />

  <ShareModal v-bind="commonStore.shareModal.props" />

  <SlideOverModal
    v-if="Object.keys(commonStore.slideOverModal.props)?.length > 0"
    v-bind="commonStore.slideOverModal.props"
  />

  <QrCodeModal v-bind="commonStore.qrCodeModal.props" />
  </template>
</template>
